import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { IoArrowBack } from "react-icons/io5";
import axios from "axios";
import { BACKEND_HOST } from "../../../api";
import { getAccessToken } from "../../../api/auth";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import useAutoLoadMore from "../../../hooks/useAutoLoadMore";
import {
  Card,
  CardHeader,
  StatusBadge,
  StatusDot,
  OrderInfo,
  OrderId,
  OrderDate,
  ProductName,
  ActionButtons,
  DetailButton,
  ContentArea,
} from "../../../Components/Common";
import { LoadingSpinner, EmptyMessage, FilterBadges } from "../../../Components/Common";
import { getStatusText } from "../../../utils/statusUtils";
import { formatDate, formatOrderId } from "../../../utils/formatters";

const FILTER_OPTIONS = ["전체", "사용가능", "완료"];
const ACTIVE_STATES = ["결제완료", "부분취소", "배송준비중", "배송중", "주문접수"];
const DONE_STATES = ["배송완료", "취소요청", "취소완료"];

export default function ShoppingPurchaseList() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("전체");
  const accessToken = getAccessToken();

  const query = useInfiniteQuery({
    queryKey: ["shopping-order-list"],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await axios.get(`${BACKEND_HOST}/shop/my-order-list/?page=${pageParam}`, {
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      });
      return res.data;
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.next) {
        const match = lastPage.next.match(/page=(\d+)/);
        if (match) return match[1];
      }
      return undefined;
    },
  });

  const loadMoreRef = useAutoLoadMore({
    enabled: true,
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    onLoadMore: query.fetchNextPage,
    rootMargin: "160px 0px",
  });

  const allResults: any[] = (query.data?.pages.flatMap((p: any) => p.results) ?? []).filter(
    (order: any) => {
      const s = order.delivery?.state || order.state;
      if (filter === "사용가능") return ACTIVE_STATES.includes(s);
      if (filter === "완료") return DONE_STATES.includes(s);
      return true;
    },
  );

  return (
    <MobileViewContainer>
      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_0_#eee]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
          >
            <IoArrowBack size={20} />
          </button>
          <h1 className="text-base font-semibold" style={{ fontFamily: "Gmarket-M" }}>
            쇼핑 내역
          </h1>
        </div>

        <FilterBadges options={FILTER_OPTIONS} activeFilter={filter} onFilterChange={setFilter} />

        <ContentArea>
          {query.isLoading ? (
            <LoadingSpinner height="200px" />
          ) : allResults.length === 0 ? (
            <EmptyMessage message="주문 내역이 없습니다." />
          ) : (
            <>
              {allResults.map((order: any) => {
                const deliveryState = order.delivery?.state;
                const displayState = deliveryState || order.state;
                return (
                  <Card key={order.id}>
                    <CardHeader>
                      <StatusBadge status={displayState}>
                        <StatusDot status={displayState} />
                        {getStatusText(displayState)}
                      </StatusBadge>
                      <OrderInfo>
                        <OrderId>{formatOrderId(order.id, 8, "S-")}</OrderId>
                        <OrderDate>{formatDate(order.order_date)}</OrderDate>
                      </OrderInfo>
                    </CardHeader>

                    {order.shopping_products?.map((product: any, idx: number) => (
                      <ProductName key={idx}>{product.name}</ProductName>
                    ))}

                    {deliveryState && (
                      <div className="mb-2 text-sm text-gray-500">
                        배송: {deliveryState}
                      </div>
                    )}

                    <ActionButtons>
                      <DetailButton onClick={() => navigate("/my-page/shopping-purchase")}>
                        상세보기
                      </DetailButton>
                    </ActionButtons>
                  </Card>
                );
              })}

              {(query.hasNextPage || query.isFetchingNextPage) && (
                <div
                  ref={loadMoreRef}
                  className="flex justify-center py-4 text-sm text-gray-500"
                  style={{ minHeight: 48 }}
                >
                  {query.isFetchingNextPage
                    ? "주문 내역을 더 불러오는 중..."
                    : "아래로 스크롤하면 더 불러옵니다."}
                </div>
              )}
            </>
          )}
        </ContentArea>
      </div>
    </MobileViewContainer>
  );
}
